/**
 * gamepad.ts — ONE polled pad service and ONE binding table for the whole game.
 *
 * Atlas can be played end to end on a controller: on foot, in a hovercraft,
 * flying a ship, in the space sector, and through the windows and menus.
 *
 * WHY NOT SYNTHETIC KEY EVENTS. Faking a `KeyboardEvent` would have been one
 * file instead of five, and it is wrong twice over: this codebase has a standing
 * law against it (the combat wave routes input through bus events precisely so
 * one attack path exists), and a synthetic key bypasses every gate the real
 * handlers check — pointer lock, the loading gate, edit mode. Each consumer asks
 * this service for the ACTIONS it cares about, exactly as it already asks its own
 * key set.
 *
 * The hardware realities (secure context, the user-gesture rule, non-standard
 * layouts with the triggers in `axes`, Firefox's binary triggers, positional
 * indices) are handled once in `gamepad_ride.ts` and reused here — see its
 * header for the sources.
 */
import {
  padAxis, padSteerCurve, padButtonValue, padProfile, padTriggers,
  PAD_STICK_DEADZONE, type PadLike, type PadProfile, type PadButtonLike,
} from './gamepad_ride.js';

// ── The binding table ────────────────────────────────────────────────────────
// Standard-layout indices, named by POSITION (the Switch Pro reports B at 0).
// One place to read, one place to change, and the harness asserts no two actions
// in the same context share a button.
export const PAD_BUTTON = {
  faceDown: 0, faceRight: 1, faceLeft: 2, faceUp: 3,
  bumperL: 4, bumperR: 5, triggerL: 6, triggerR: 7,
  select: 8, start: 9, stickL: 10, stickR: 11,
  dpadUp: 12, dpadDown: 13, dpadLeft: 14, dpadRight: 15,
} as const;

/** Every action the pad can ask for, across every part of the game. */
export type PadActionName =
  // on foot
  | 'jump' | 'sprint' | 'crouch' | 'attack' | 'guard' | 'interact'
  | 'weaponNext' | 'weaponPrev' | 'zoomIn' | 'zoomOut'
  // flight (ship + sector)
  | 'boost' | 'fire' | 'levelOut' | 'rollLeft' | 'rollRight'
  // shell
  | 'menu' | 'map' | 'inventory'
  // ui
  | 'uiAccept' | 'uiBack' | 'uiUp' | 'uiDown' | 'uiLeft' | 'uiRight'
  | 'uiTabNext' | 'uiTabPrev'
  // edit mode
  | 'editNudgeLeft' | 'editNudgeRight' | 'editNudgeUp' | 'editNudgeDown'
  | 'editToolNext' | 'editToolPrev' | 'editCommit' | 'editDeselect';

/** A binding is a set of buttons — any of them satisfies the action. These are
 *  the DEFAULTS; the live table (`padBindings()`) is these overridden by whatever
 *  the player has rebound, and every read goes through the live one. */
export const PAD_DEFAULT_BINDINGS: Readonly<Record<PadActionName, readonly number[]>> = {
  // ── On foot: the console-shooter layout a player already knows. ──
  jump:       [PAD_BUTTON.faceDown],
  crouch:     [PAD_BUTTON.faceRight],
  interact:   [PAD_BUTTON.faceLeft],
  weaponNext: [PAD_BUTTON.faceUp, PAD_BUTTON.bumperR],
  weaponPrev: [PAD_BUTTON.bumperL],
  attack:     [PAD_BUTTON.triggerR],
  guard:      [PAD_BUTTON.triggerL],
  sprint:     [PAD_BUTTON.stickL],
  zoomIn:     [PAD_BUTTON.dpadUp],
  zoomOut:    [PAD_BUTTON.dpadDown],
  // ── Flight. A racer and a spacecraft have different conventions and the game
  //    tells you which you are in: in the RIDE the triggers are throttle and
  //    brake (gamepad_ride.ts), while in FLIGHT the throttle is the left stick
  //    and the triggers are the guns, which is what every space sim does. ──
  fire:       [PAD_BUTTON.triggerR],
  boost:      [PAD_BUTTON.triggerL],
  levelOut:   [PAD_BUTTON.faceDown],
  rollLeft:   [PAD_BUTTON.bumperL],
  rollRight:  [PAD_BUTTON.bumperR],
  // ── Shell. ──
  menu:       [PAD_BUTTON.start],
  map:        [PAD_BUTTON.select],
  inventory:  [PAD_BUTTON.dpadRight],
  // ── UI. Accept/back sit on the same faces as jump/crouch, which is the
  //    convention everywhere; the contexts never overlap (a window is up or the
  //    world is). ──
  uiAccept:   [PAD_BUTTON.faceDown],
  uiBack:     [PAD_BUTTON.faceRight],
  uiUp:       [PAD_BUTTON.dpadUp],
  uiDown:     [PAD_BUTTON.dpadDown],
  uiLeft:     [PAD_BUTTON.dpadLeft],
  uiRight:    [PAD_BUTTON.dpadRight],
  uiTabNext:  [PAD_BUTTON.bumperR],
  uiTabPrev:  [PAD_BUTTON.bumperL],
  // ── Edit mode. The sticks FLY the camera, so the gizmo takes the d-pad:
  //    discrete, repeatable nudges are what an editor actually wants, and they
  //    do not fight the flying. ──
  editNudgeLeft:  [PAD_BUTTON.dpadLeft],
  editNudgeRight: [PAD_BUTTON.dpadRight],
  editNudgeUp:    [PAD_BUTTON.dpadUp],
  editNudgeDown:  [PAD_BUTTON.dpadDown],
  editToolNext:   [PAD_BUTTON.bumperR],
  editToolPrev:   [PAD_BUTTON.bumperL],
  editCommit:     [PAD_BUTTON.faceDown],
  editDeselect:   [PAD_BUTTON.faceRight],
};

/** Every action name, in the order a settings list should show them. */
export const PAD_ACTIONS = Object.keys(PAD_DEFAULT_BINDINGS) as PadActionName[];

/** Human-readable binding names, for the on-screen hint. */
export const PAD_BUTTON_LABEL: Readonly<Record<number, string>> = {
  0: 'A', 1: 'B', 2: 'X', 3: 'Y', 4: 'LB', 5: 'RB', 6: 'LT', 7: 'RT',
  8: 'Back', 9: 'Start', 10: 'L3', 11: 'R3',
  12: 'D-pad ↑', 13: 'D-pad ↓', 14: 'D-pad ←', 15: 'D-pad →',
};

/** Hold before a held UI direction starts repeating (s), then the rate (s). */
export const PAD_REPEAT_DELAY_S = 0.42;
export const PAD_REPEAT_RATE_S = 0.11;
/** A stick past this counts as a UI direction (it is a d-pad substitute there). */
export const PAD_UI_STICK_THRESHOLD = 0.6;
/** Look sensitivity: radians per second at full stick deflection. */
export const PAD_LOOK_RATE = 2.9;
/** Look response curve — the same shape the steering uses, for the same reason. */
export const PAD_LOOK_LINEARITY = 0.35;

/** One frame of pad state, already deadzoned and curved. */
export interface PadSnapshot {
  present: boolean;
  profile: PadProfile | null;
  id: string;
  mapping: string;
  /** Left stick, deadzoned. +x right, +y DOWN (the raw axis convention). */
  lx: number; ly: number;
  /** Right stick, deadzoned. */
  rx: number; ry: number;
  /** Triggers, 0..1, however this pad exposes them. */
  lt: number; rt: number;
  /** Per-button held state this frame. */
  down: readonly boolean[];
  /** Buttons that went down THIS frame. */
  edge: readonly boolean[];
}

const EMPTY: PadSnapshot = {
  present: false, profile: null, id: '', mapping: '',
  lx: 0, ly: 0, rx: 0, ry: 0, lt: 0, rt: 0, down: [], edge: [],
};

interface PadService {
  snap: PadSnapshot;
  /** The LIVE binding table: the defaults with the player's rebinds over them. */
  binds: Record<PadActionName, number[]>;
  /** Rumble: the player's switch and strength. */
  rumble: boolean;
  rumbleGain: number;
  /** An XR session's controllers, fed in place of a desktop pad. */
  xr: PadSnapshot | null;
  /** A rebind waiting for a button press, and its last outcome. */
  capture: PadActionName | null;
  captureLast: PadCaptureResult | null;
  captureSeq: number;
  /** The pad object THIS frame's poll read. Held so every consumer in the frame
   *  sees one consistent snapshot and `getGamepads()` is called exactly once —
   *  the ride's fixed tick can run several times per frame. */
  pad: PadLike | null;
  prevDown: boolean[];
  /** Index of the pad we are driving, and its measured profile. */
  index: number | null;
  rest: number[] | null;
  profile: PadProfile | null;
  /** Repeat clocks, per action. */
  held: Map<string, number>;
  /** True once a pad has ever been seen — drives the "pad detected" hint. */
  everSeen: boolean;
  /** Set false by the settings toggle. */
  enabled: boolean;
}

/** The player's opt-out, remembered. Default ON: a pad that is plugged in
 *  should simply work, and it costs nothing when there is none. */
const PAD_LS = 'atlas:gamepad';
const BINDS_LS = 'atlas:gamepad-binds';
const RUMBLE_LS = 'atlas:gamepad-rumble';
const RUMBLE_GAIN_LS = 'atlas:gamepad-rumble-gain';
function loadFlag(key: string, dflt: boolean): boolean {
  try { const v = localStorage.getItem(key); return v === null ? dflt : v !== 'off'; } catch { return dflt; }
}
function loadEnabled(): boolean { return loadFlag(PAD_LS, true); }
function loadGain(): number {
  try { const v = Number(localStorage.getItem(RUMBLE_GAIN_LS)); return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 0.75; } catch { return 0.75; }
}
/** The defaults with the player's saved rebinds over them. A saved entry that
 *  names an action or a button this build no longer has is dropped, so an old
 *  save can never leave an action unbound or bound to nothing real. */
function loadBinds(): Record<PadActionName, number[]> {
  const out = {} as Record<PadActionName, number[]>;
  for (const a of PAD_ACTIONS) out[a] = [...PAD_DEFAULT_BINDINGS[a]];
  try {
    const raw = localStorage.getItem(BINDS_LS);
    if (!raw) return out;
    const saved = JSON.parse(raw) as Record<string, number[]>;
    for (const [k, v] of Object.entries(saved)) {
      if (!(k in out) || !Array.isArray(v)) continue;
      const clean = v.filter((i) => Number.isInteger(i) && i >= 0 && i <= 15);
      if (clean.length) out[k as PadActionName] = clean;
    }
  } catch { /* private mode, or a corrupt save — the defaults stand */ }
  return out;
}

const G = globalThis as unknown as { __atlasPad?: PadService };
function svc(): PadService {
  if (!G.__atlasPad) {
    G.__atlasPad = {
      snap: EMPTY, pad: null, prevDown: [], index: null, rest: null, profile: null,
      held: new Map(), everSeen: false, enabled: loadEnabled(),
      binds: loadBinds(), rumble: loadFlag(RUMBLE_LS, true), rumbleGain: loadGain(), xr: null,
      capture: null, captureLast: null, captureSeq: 0,
    };
  }
  return G.__atlasPad;
}

/** The raw pad, or null. Never throws: the API is secure-context only and can
 *  be absent entirely, and it returns nulls until the player presses something
 *  while the page is focused. */
export function rawPad(): PadLike | null {
  const nav = (typeof navigator === 'undefined' ? null : navigator) as
    (Navigator & { getGamepads?: () => (Gamepad | null)[] }) | null;
  if (!nav || typeof nav.getGamepads !== 'function') return null;
  let pads: (Gamepad | null)[];
  try { pads = nav.getGamepads(); } catch { return null; }
  const s = svc();
  let pick: Gamepad | null = null;
  for (const g of pads) {
    if (!g || g.connected === false || g.axes.length < 2) continue;
    if (s.index !== null && g.index === s.index) { pick = g; break; }
    if (!pick) pick = g;
  }
  return pick;
}

/**
 * Poll once per frame. Everything else in the game reads the snapshot, so the
 * whole game sees ONE consistent frame of pad state and edges fire exactly once.
 */
export function padPoll(dt: number): PadSnapshot {
  const s = svc();
  // A headset's controllers arrive through `padFeedXr` on the XR frame clock and
  // OWN the snapshot while a session presents — polling `getGamepads()` over the
  // top would clear them every frame and nothing in the headset would respond.
  if (s.xr) return s.snap;
  const pad = s.enabled ? rawPad() : null;
  s.pad = pad;
  if (!pad) {
    s.index = null; s.rest = null; s.profile = null;
    s.prevDown = [];
    s.held.clear();
    s.snap = EMPTY;
    return s.snap;
  }
  s.everSeen = true;
  if (pad.index !== undefined && pad.index !== s.index) { s.index = pad.index; s.rest = null; s.profile = null; }
  // The resting snapshot finds triggers that live in `axes` (they rest at −1),
  // and can only be trusted on a frame where nothing is held.
  if (!s.rest && pad.buttons.every((b) => !b.pressed && b.value <= 0.02)) {
    s.rest = Array.from(pad.axes);
    s.profile = null;
  }
  if (!s.profile) s.profile = padProfile(pad, s.rest);
  const { lt, rt } = padTriggers(pad, s.profile);
  const down: boolean[] = [];
  const edge: boolean[] = [];
  for (let i = 0; i < pad.buttons.length; i++) {
    const d = padButtonValue(pad.buttons[i]) > 0.5;
    down[i] = d;
    edge[i] = d && !s.prevDown[i];
  }
  s.prevDown = down.slice();
  s.snap = {
    present: true, profile: s.profile, id: pad.id ?? '', mapping: pad.mapping ?? '',
    lx: padAxis(pad.axes[0] ?? 0), ly: padAxis(pad.axes[1] ?? 0),
    rx: padAxis(pad.axes[2] ?? 0), ry: padAxis(pad.axes[3] ?? 0),
    lt, rt, down, edge,
  };
  void dt;
  return s.snap;
}

// ── XR ───────────────────────────────────────────────────────────────────────
// In a headset the controllers are NOT in `navigator.getGamepads()` — they hang
// off `XRInputSource.gamepad` and use the xr-standard mapping. Rather than teach
// every consumer a second input shape, the XR module feeds them through here and
// they arrive as an ordinary snapshot, so the SAME actions and the SAME rebinds
// work in the headset.
//
// xr-standard → our standard-layout indices, by what the hand actually does:
//   trigger (0)  → the trigger on that side       squeeze (1) → that bumper
//   stick press (3) → that stick press            A/X (4) → face down / left
//   B/Y (5)      → face right / up                menu (6, when a runtime has it) → Start
export const XR_BUTTON_MAP: Readonly<Record<'left' | 'right', Readonly<Record<number, number>>>> = {
  right: { 0: PAD_BUTTON.triggerR, 1: PAD_BUTTON.bumperR, 3: PAD_BUTTON.stickR, 4: PAD_BUTTON.faceDown, 5: PAD_BUTTON.faceRight, 6: PAD_BUTTON.start },
  left:  { 0: PAD_BUTTON.triggerL, 1: PAD_BUTTON.bumperL, 3: PAD_BUTTON.stickL, 4: PAD_BUTTON.faceLeft, 5: PAD_BUTTON.faceUp,    6: PAD_BUTTON.select },
};

/** What we need from an `XRInputSource` — a plain object in the harness. */
export interface XrSourceLike {
  handedness?: string;
  gamepad?: { axes: readonly (number | null)[]; buttons: readonly PadButtonLike[] } | null;
}

/**
 * Build a snapshot from an XR session's controllers. PURE apart from the edge
 * bookkeeping it shares with the desktop path, so a press still fires once.
 *
 * The thumbsticks land on axes [2,3] on most runtimes and [0,1] on some; the XR
 * module already learned that for locomotion and the same fallback is used here.
 */
export function padFeedXr(sources: readonly XrSourceLike[] | null | undefined): PadSnapshot {
  const s = svc();
  if (!s.enabled || !sources || !sources.length) { s.xr = null; return s.snap; }
  const down: boolean[] = new Array(16).fill(false);
  let lx = 0, ly = 0, rx = 0, ry = 0, lt = 0, rt = 0, any = false;
  for (const src of sources) {
    const gp = src.gamepad;
    if (!gp) continue;
    const hand = src.handedness === 'left' ? 'left' : src.handedness === 'right' ? 'right' : null;
    if (!hand) continue;
    any = true;
    const ax = (i: number): number => { const v = gp.axes[i]; return typeof v === 'number' ? v : 0; };
    const useHigh = gp.axes.length > 2 && (ax(2) !== 0 || ax(3) !== 0 || Math.abs(ax(0)) + Math.abs(ax(1)) < 0.01);
    const sx = padAxis(useHigh ? ax(2) : ax(0));
    const sy = padAxis(useHigh ? ax(3) : ax(1));
    if (hand === 'left') { lx = sx; ly = sy; } else { rx = sx; ry = sy; }
    const map = XR_BUTTON_MAP[hand];
    for (const [srcIdx, ourIdx] of Object.entries(map)) {
      const v = padButtonValue(gp.buttons[Number(srcIdx)]);
      if (v > 0.5) down[ourIdx] = true;
      if (Number(srcIdx) === 0) { if (hand === 'left') lt = v; else rt = v; }
    }
  }
  if (!any) { s.xr = null; return s.snap; }
  const edge: boolean[] = [];
  for (let i = 0; i < down.length; i++) edge[i] = down[i] && !s.prevDown[i];
  s.prevDown = down.slice();
  s.everSeen = true;
  s.snap = {
    present: true, profile: null, id: 'xr-controllers', mapping: 'xr-standard',
    lx, ly, rx, ry, lt, rt, down, edge,
  };
  s.xr = s.snap;
  return s.snap;
}

/** True while the snapshot is coming from a headset, not a desktop pad. */
export function padXrActive(): boolean { return svc().xr !== null; }

/** The last polled frame. */
export function padSnap(): PadSnapshot { return svc().snap; }
/** The pad object this frame's poll read — never a fresh `getGamepads()` call. */
export function padCached(): PadLike | null { return svc().pad; }
/** True once any pad has been seen this session (for the hint). */
export function padEverSeen(): boolean { return svc().everSeen; }
/** The player's opt-out. */
export function padEnabled(): boolean { return svc().enabled; }
/** The XR session ended: hand the snapshot back to the desktop poll. */
export function padClearXr(): void {
  const s = svc();
  s.xr = null; s.snap = EMPTY; s.prevDown = []; s.held.clear();
}

export function setPadEnabled(on: boolean): void {
  const s = svc();
  s.enabled = on;
  if (!on) { s.snap = EMPTY; s.pad = null; s.xr = null; s.prevDown = []; s.held.clear(); }
  try { localStorage.setItem(PAD_LS, on ? 'on' : 'off'); } catch { /* private mode */ }
}

/** Is any button bound to this action held? */
export function padHeld(action: PadActionName): boolean {
  const s = svc();
  const { down } = s.snap;
  for (const i of s.binds[action]) if (down[i]) return true;
  return false;
}

/** Did any button bound to this action go down THIS frame? */
export function padPressed(action: PadActionName): boolean {
  const s = svc();
  const { edge } = s.snap;
  for (const i of s.binds[action]) if (edge[i]) return true;
  return false;
}

/**
 * A press that REPEATS while held — the UI directions. Returns true on the
 * initial press, then at a steady rate after the delay. `key` separates clocks
 * so two directions held at once do not share one.
 */
export function padRepeat(action: PadActionName, dt: number, extraHeld = false): boolean {
  const s = svc();
  const on = padHeld(action) || extraHeld;
  const prev = s.held.get(action);
  if (!on) { s.held.delete(action); return false; }
  if (prev === undefined) { s.held.set(action, 0); return true; }
  const t = prev + dt;
  if (prev < PAD_REPEAT_DELAY_S && t >= PAD_REPEAT_DELAY_S) { s.held.set(action, PAD_REPEAT_DELAY_S); return true; }
  if (prev >= PAD_REPEAT_DELAY_S && t - prev >= 0) {
    const since = t - PAD_REPEAT_DELAY_S;
    const prevSince = prev - PAD_REPEAT_DELAY_S;
    if (Math.floor(since / PAD_REPEAT_RATE_S) > Math.floor(prevSince / PAD_REPEAT_RATE_S)) { s.held.set(action, t); return true; }
  }
  s.held.set(action, t);
  return false;
}

/** Clear every repeat clock (on a context switch, so nothing carries over). */
export function padClearRepeats(): void { svc().held.clear(); }

/**
 * The look delta (radians) the right stick asks for this frame, on the same
 * curve the steering uses: the first part of the travel turns less, which is
 * what makes a 2 cm stick usable for aiming.
 */
export function padLook(dt: number): { dx: number; dy: number } {
  const { rx, ry, present } = svc().snap;
  if (!present) return { dx: 0, dy: 0 };
  return {
    dx: padSteerCurve(rx, PAD_LOOK_LINEARITY) * PAD_LOOK_RATE * dt,
    dy: padSteerCurve(ry, PAD_LOOK_LINEARITY) * PAD_LOOK_RATE * dt,
  };
}

/** The movement the left stick asks for: forward and strafe, −1..1 each. */
export function padMove(): { f: number; s: number } {
  const { lx, ly, present } = svc().snap;
  if (!present) return { f: 0, s: 0 };
  return { f: -ly, s: lx };          // stick UP (−y) is forward, right (+x) strafes right
}

/** A UI direction, repeating while held; the left stick doubles for the d-pad. */
export function padUiStep(dt: number): { x: number; y: number } {
  const { lx, ly } = svc().snap;
  const up = padRepeat('uiUp', dt, ly <= -PAD_UI_STICK_THRESHOLD);
  const down = padRepeat('uiDown', dt, ly >= PAD_UI_STICK_THRESHOLD);
  const left = padRepeat('uiLeft', dt, lx <= -PAD_UI_STICK_THRESHOLD);
  const right = padRepeat('uiRight', dt, lx >= PAD_UI_STICK_THRESHOLD);
  return { x: (right ? 1 : 0) - (left ? 1 : 0), y: (down ? 1 : 0) - (up ? 1 : 0) };
}

/** Actions that share a button are only safe when their CONTEXTS never overlap.
 *  The harness uses this to hold that promise: within one context, a button may
 *  serve at most one action. */
export const PAD_CONTEXTS: Readonly<Record<string, readonly PadActionName[]>> = {
  foot: ['jump', 'crouch', 'interact', 'weaponNext', 'weaponPrev', 'attack', 'guard', 'sprint', 'zoomIn', 'zoomOut', 'menu', 'map', 'inventory'],
  flight: ['boost', 'fire', 'levelOut', 'rollLeft', 'rollRight', 'menu', 'map'],
  ui: ['uiAccept', 'uiBack', 'uiUp', 'uiDown', 'uiLeft', 'uiRight', 'uiTabNext', 'uiTabPrev', 'menu'],
  edit: ['editNudgeLeft', 'editNudgeRight', 'editNudgeUp', 'editNudgeDown', 'editToolNext', 'editToolPrev', 'editCommit', 'editDeselect', 'menu'],
};

/** The bindings as readable text, for the on-screen hint and the settings row. */
export function padBindingText(action: PadActionName): string {
  return svc().binds[action].map((i) => PAD_BUTTON_LABEL[i] ?? `#${i}`).join(' / ');
}

// ── Rebinding ────────────────────────────────────────────────────────────────

/** The live table (a copy — callers must not mutate the service's own). */
export function padBindings(): Record<PadActionName, number[]> {
  const s = svc();
  const out = {} as Record<PadActionName, number[]>;
  for (const a of PAD_ACTIONS) out[a] = [...s.binds[a]];
  return out;
}

/** Which CONTEXTS an action belongs to. A rebind is only legal if it creates no
 *  collision inside any context the action lives in — the whole binding scheme
 *  rests on a button meaning one thing per context. */
export function contextsOf(action: PadActionName): string[] {
  return Object.entries(PAD_CONTEXTS).filter(([, list]) => list.includes(action)).map(([k]) => k);
}

/**
 * Would binding `action` to `buttons` collide? Returns the conflicting action
 * names — empty means the rebind is legal. PURE over a supplied table so the
 * settings UI can ask before committing.
 */
export function bindingConflicts(
  table: Readonly<Record<PadActionName, readonly number[]>>,
  action: PadActionName,
  buttons: readonly number[],
): PadActionName[] {
  const mine = new Set(buttons);
  const ctxs = new Set(contextsOf(action));
  const out: PadActionName[] = [];
  for (const other of PAD_ACTIONS) {
    if (other === action) continue;
    if (!contextsOf(other).some((c) => ctxs.has(c))) continue;      // never share a frame
    if (table[other].some((i) => mine.has(i)) && !out.includes(other)) out.push(other);
  }
  return out;
}

/** Rebind an action. Refuses a collision inside a shared context, and refuses to
 *  leave an action with nothing bound. Returns the conflicts on refusal. */
export function setPadBinding(action: PadActionName, buttons: readonly number[]): PadActionName[] {
  const s = svc();
  const clean = buttons.filter((i) => Number.isInteger(i) && i >= 0 && i <= 15);
  if (!clean.length) return [action];
  const clash = bindingConflicts(s.binds, action, clean);
  if (clash.length) return clash;
  s.binds[action] = [...clean];
  saveBinds();
  return [];
}

/** Put every action back to its default. */
export function resetPadBindings(): void {
  const s = svc();
  for (const a of PAD_ACTIONS) s.binds[a] = [...PAD_DEFAULT_BINDINGS[a]];
  try { localStorage.removeItem(BINDS_LS); } catch { /* private mode */ }
}

function saveBinds(): void {
  const s = svc();
  const diff: Record<string, number[]> = {};
  for (const a of PAD_ACTIONS) {
    const d = PAD_DEFAULT_BINDINGS[a];
    if (s.binds[a].length !== d.length || s.binds[a].some((v, i) => v !== d[i])) diff[a] = s.binds[a];
  }
  try {
    if (Object.keys(diff).length) localStorage.setItem(BINDS_LS, JSON.stringify(diff));
    else localStorage.removeItem(BINDS_LS);
  } catch { /* private mode */ }
}

// A rebind in progress. The service owns it so the UI navigator can stand down
// while it runs — otherwise the very press being captured would also activate
// whatever control the focus happens to be on.
export interface PadCaptureResult { action: PadActionName; button: number | null; conflicts: PadActionName[] }

export function padBeginCapture(action: PadActionName): void { svc().capture = action; }
export function padCancelCapture(): void { svc().capture = null; }
export function padCaptureActive(): PadActionName | null { return svc().capture; }
/** Bumped whenever a capture resolves, so a settings panel knows to re-render. */
export function padCaptureSeq(): number { return svc().captureSeq; }
export function padLastCapture(): PadCaptureResult | null { return svc().captureLast; }

/**
 * Consume one frame toward a pending capture. Returns true while a capture is
 * running (the caller should not let anything else read the pad this frame).
 */
export function padCaptureStep(): boolean {
  const s = svc();
  if (!s.capture) return false;
  const btn = padCaptureButton();
  if (btn === null) return true;
  const action = s.capture;
  const conflicts = setPadBinding(action, [btn]);
  s.capture = null;
  s.captureLast = { action, button: conflicts.length ? null : btn, conflicts };
  s.captureSeq++;
  return true;
}

/** The first button that went down this frame, for a "press a button…" prompt. */
export function padCaptureButton(): number | null {
  const { edge } = svc().snap;
  for (let i = 0; i < edge.length && i <= 15; i++) if (edge[i]) return i;
  return null;
}

// ── Rumble ───────────────────────────────────────────────────────────────────

/** The haptic vocabulary. Every one is short: a pad that buzzes for long reads
 *  as broken, and a player feels the ENVELOPE far more than the amplitude. */
export type RumbleKind = 'hit' | 'hurt' | 'land' | 'crash' | 'scrape' | 'fire' | 'ui';

/** PURE: what a kind asks for, before the player's gain. */
export function rumbleFor(kind: RumbleKind, scale = 1): { strong: number; weak: number; ms: number } {
  const k = Math.max(0, Math.min(1, scale));
  switch (kind) {
    case 'hurt':  return { strong: 0.85, weak: 0.5, ms: 220 };     // a blow TAKEN is the loudest thing a pad says
    case 'crash': return { strong: 0.7 + 0.3 * k, weak: 0.4, ms: 180 };
    case 'hit':   return { strong: 0.45, weak: 0.35, ms: 90 };     // a landed swing
    case 'land':  return { strong: 0.3 + 0.4 * k, weak: 0.2, ms: 120 };
    case 'fire':  return { strong: 0.12, weak: 0.3, ms: 55 };      // weapons tick, they do not thump
    case 'scrape':return { strong: 0.05, weak: 0.22 * k, ms: 60 }; // a wall graze, scaled by how hard
    case 'ui':    return { strong: 0, weak: 0.18, ms: 35 };
  }
}

export function padRumbleEnabled(): boolean { return svc().rumble; }
export function setPadRumble(on: boolean): void {
  svc().rumble = on;
  try { localStorage.setItem(RUMBLE_LS, on ? 'on' : 'off'); } catch { /* private mode */ }
}
export function padRumbleGain(): number { return svc().rumbleGain; }
/** Does the connected pad actually have a haptic actuator? Most do not on
 *  Firefox, nor over some Bluetooth stacks — the settings row says so rather
 *  than leaving a player wondering why nothing buzzes. */
export function padHasActuator(): boolean {
  const pad = svc().pad as (PadLike & { vibrationActuator?: { playEffect?: unknown } }) | null;
  return typeof pad?.vibrationActuator?.playEffect === 'function';
}
export function setPadRumbleGain(v: number): void {
  svc().rumbleGain = Math.max(0, Math.min(1, v));
  try { localStorage.setItem(RUMBLE_GAIN_LS, String(svc().rumbleGain)); } catch { /* private mode */ }
}

/**
 * Buzz the pad. Silently does nothing without a pad, with rumble switched off,
 * or on a controller that has no actuator — which is most of them on Firefox and
 * every one over some Bluetooth stacks. NEVER awaited and never throws: a failed
 * haptic must not interrupt a frame.
 */
export function padRumble(kind: RumbleKind, scale = 1): boolean {
  const s = svc();
  if (!s.rumble || !s.snap.present) return false;
  const pad = s.pad as (PadLike & { vibrationActuator?: { playEffect?: (t: string, p: unknown) => Promise<unknown> } }) | null;
  const act = pad?.vibrationActuator;
  if (!act || typeof act.playEffect !== 'function') return false;
  const r = rumbleFor(kind, scale);
  const g = s.rumbleGain;
  try {
    void act.playEffect('dual-rumble', {
      duration: r.ms, startDelay: 0,
      strongMagnitude: Math.min(1, r.strong * g), weakMagnitude: Math.min(1, r.weak * g),
    })?.catch?.(() => { /* the runtime refused — not a frame's problem */ });
  } catch { return false; }
  return true;
}

export { PAD_STICK_DEADZONE };
